import { getFunctions, httpsCallable } from 'firebase/functions';
import { FinancialPlanData } from '../types/financialPlan';
import {
  FinancialPlanShareMeta,
  FinancialPlanShareOwnerInfo,
  FinancialPlanSharePayload,
} from '../types/financialPlanShare';

const regionFunctions = () => getFunctions(undefined, 'us-central1');

const PIN_STORAGE_PREFIX = 'lgc_plan_share_pin_';

export function financialPlanShareUrl(slug: string): string {
  return `${window.location.origin}/plan-share/${slug}`;
}

export function financialPlanShareClipboardText(slug: string, accessPin: string): string {
  return `${financialPlanShareUrl(slug)}\nPIN: ${accessPin}`;
}

export function storeSharePin(slug: string, pin: string): void {
  try {
    sessionStorage.setItem(`${PIN_STORAGE_PREFIX}${slug}`, pin);
    sessionStorage.setItem(`lgc_plan_share_verified_${slug}`, 'true');
  } catch {
    /* ignore */
  }
}

export function readStoredSharePin(slug: string): string | null {
  try {
    return sessionStorage.getItem(`${PIN_STORAGE_PREFIX}${slug}`);
  } catch {
    return null;
  }
}

export function clearStoredSharePin(slug: string): void {
  try {
    sessionStorage.removeItem(`${PIN_STORAGE_PREFIX}${slug}`);
    sessionStorage.removeItem(`lgc_plan_share_verified_${slug}`);
  } catch {
    /* ignore */
  }
}

export async function getFinancialPlanShareMeta(slug: string): Promise<FinancialPlanShareMeta> {
  const fn = httpsCallable<{ slug: string }, FinancialPlanShareMeta>(
    regionFunctions(),
    'getFinancialPlanShareMeta',
  );
  const res = await fn({ slug });
  return res.data;
}

export async function fetchFinancialPlanShare(
  slug: string,
  pin: string,
  options?: { recordView?: boolean },
): Promise<FinancialPlanSharePayload> {
  const fn = httpsCallable<
    { slug: string; pin: string; recordView?: boolean },
    FinancialPlanSharePayload
  >(regionFunctions(), 'fetchFinancialPlanShare');
  const res = await fn({ slug, pin, recordView: options?.recordView });
  return res.data;
}

export async function verifyFinancialPlanShare(
  slug: string,
  pin: string,
): Promise<FinancialPlanSharePayload> {
  return fetchFinancialPlanShare(slug, pin, { recordView: true });
}

export async function pollFinancialPlanShare(
  slug: string,
  pin: string,
  sinceRevision?: number,
): Promise<FinancialPlanSharePayload & { unchanged?: boolean }> {
  const fn = httpsCallable<
    { slug: string; pin: string; sinceRevision?: number },
    FinancialPlanSharePayload & { unchanged?: boolean }
  >(regionFunctions(), 'pollFinancialPlanShare');
  const res = await fn({ slug, pin, sinceRevision });
  return res.data;
}

export async function getFinancialPlanShareForOwner(
  planId: string | null,
): Promise<FinancialPlanShareOwnerInfo | null> {
  const fn = httpsCallable<
    { planId: string | null },
    { share: FinancialPlanShareOwnerInfo | null }
  >(regionFunctions(), 'getFinancialPlanShareForOwner');
  const res = await fn({ planId });
  return res.data.share;
}

export async function publishFinancialPlanShare(input: {
  planId: string | null;
  planName: string;
  planData: FinancialPlanData;
  regeneratePin?: boolean;
}): Promise<FinancialPlanShareOwnerInfo> {
  const fn = httpsCallable<
    {
      planId: string | null;
      planName: string;
      planData: FinancialPlanData;
      regeneratePin?: boolean;
    },
    { share: FinancialPlanShareOwnerInfo }
  >(regionFunctions(), 'publishFinancialPlanShare');
  const res = await fn(input);
  return res.data.share;
}

export async function syncFinancialPlanShare(input: {
  planId: string | null;
  planName: string;
  planData: FinancialPlanData;
}): Promise<{ updatedAt: number | null; liveRevision: number }> {
  const fn = httpsCallable<
    {
      planId: string | null;
      planName: string;
      planData: FinancialPlanData;
    },
    { updatedAt: number | null; liveRevision: number }
  >(regionFunctions(), 'syncFinancialPlanShare');
  const res = await fn(input);
  return res.data;
}

export async function revokeFinancialPlanShare(planId: string | null): Promise<void> {
  const fn = httpsCallable<{ planId: string | null }, { ok: boolean }>(
    regionFunctions(),
    'revokeFinancialPlanShare',
  );
  await fn({ planId });
}
